/**
 * WebSocket E2E helpers for Log Bot.
 * Mirrors the same RSA-OAEP + CryptoJS AES scheme / e2eInfo gate used by
 * axiosClient in apiConstant.js — kept local so we never touch core files.
 *
 * Encryption gate (same as apiConstant):
 *   ?txnEnable=<REACT_APP_DISABLE_UUID> → e2eInfo.enabled = false → plain JSON
 *   otherwise (or param absent)         → e2eInfo.enabled = true  → encrypt
 *
 * Encrypted protocol (§4.5):
 *   wss://…/log_bot/ws?x-api-headers=<RSA-encrypted-passphrase>
 *   every frame both ways: { data: "<AES-encrypted base64>" }
 */
import CryptoJS from "crypto-js";
import { v4 as uuidv4 } from "uuid";
import store from "../../../../store/store";
import { getDecryptedSessionItem } from "../../../../Constant/helper";

const EXPIRY_MS = 10 * 60 * 1000;

/**
 * Same decision as axiosClient interceptor in apiConstant.js.
 * Returns false when txnEnable disabled encryption → caller must use raw JSON.
 */
export const isE2EEnabled = (): boolean => {
  let enabled = true;
  const stored = getDecryptedSessionItem("e2eInfo");

  if (stored) {
    if (Date.now() - stored.timestamp <= EXPIRY_MS) {
      enabled = Boolean(stored.enabled);
    } else {
      sessionStorage.removeItem("e2eInfo");
    }
  }

  return enabled;
};

async function rsaEncrypt(plainText: string, publicKeyPem: string): Promise<string> {
  const binaryDerString = atob(
    publicKeyPem.replace(/-----.*?-----/g, "").replace(/\n/g, "")
  );
  const binaryDer = new Uint8Array(binaryDerString.length);
  for (let i = 0; i < binaryDerString.length; i++) {
    binaryDer[i] = binaryDerString.charCodeAt(i);
  }

  const publicKey = await window.crypto.subtle.importKey(
    "spki",
    binaryDer.buffer,
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["encrypt"]
  );

  const dataBuffer = new TextEncoder().encode(plainText);
  const encryptedBuffer = await window.crypto.subtle.encrypt(
    { name: "RSA-OAEP" },
    publicKey,
    dataBuffer
  );

  return btoa(String.fromCharCode(...new Uint8Array(encryptedBuffer)));
}

export const aesEncryptFrame = (frame: unknown, passphrase: string): string =>
  CryptoJS.AES.encrypt(JSON.stringify(frame), passphrase).toString();

export const aesDecryptFrame = <T = unknown>(
  encrypted: string,
  passphrase: string
): T | null => {
  try {
    const bytes = CryptoJS.AES.decrypt(encrypted, passphrase);
    return JSON.parse(bytes.toString(CryptoJS.enc.Utf8)) as T;
  } catch (error) {
    console.error("LogBot WS decrypt error:", error);
    return null;
  }
};

/**
 * Creates a fresh AES passphrase + RSA-wrapped x-api-headers value.
 * Returns null when E2E is disabled (caller uses plain JSON) OR when
 * publicKey is missing while E2E is on (caller should treat as hard error).
 */
export const createWsE2ESession = async (): Promise<{
  passphrase: string;
  xApiHeaders: string;
} | null> => {
  if (!isE2EEnabled()) return null;

  const publicKey = store.getState()?.AccountReducer?.publicKey;
  if (!publicKey) return null;

  const passphrase = uuidv4();
  const xApiHeaders = await rsaEncrypt(passphrase, publicKey);
  return { passphrase, xApiHeaders };
};
